import { config as rxjsConfig } from 'rxjs';

// Polyfill for Symbol.dispose (Safari compatibility)
// Safari doesn't support Explicit Resource Management (using keyword)
// See: https://github.com/tc39/proposal-explicit-resource-management
const SymbolConstructor = Symbol as unknown as Record<string, symbol | undefined>;
if (typeof SymbolConstructor.dispose === 'undefined') {
  SymbolConstructor.dispose = Symbol.for('Symbol.dispose');
}
if (typeof SymbolConstructor.asyncDispose === 'undefined') {
  SymbolConstructor.asyncDispose = Symbol.for('Symbol.asyncDispose');
}

const BENIGN_RESIZE_OBSERVER_ERRORS = [
  'resizeobserver loop completed with undelivered notifications',
  'resizeobserver loop limit exceeded',
];

const isBenignResizeObserverMessage = (message: string) =>
  BENIGN_RESIZE_OBSERVER_ERRORS.some((knownError) => message.toLowerCase().includes(knownError));

const getErrorMessage = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object' && 'message' in value) {
    const possibleMessage = (value as { message?: unknown }).message;
    if (typeof possibleMessage === 'string') {
      return possibleMessage;
    }
  }

  return '';
};

const suppressEvent = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
};

window.addEventListener(
  'error',
  (event) => {
    const errorMessage = event.message || getErrorMessage(event.error);

    if (isBenignResizeObserverMessage(errorMessage)) {
      suppressEvent(event);
    }
  },
  { capture: true }
);

window.addEventListener(
  'unhandledrejection',
  (event) => {
    if (isBenignResizeObserverMessage(getErrorMessage(event.reason))) {
      suppressEvent(event);
    }
  },
  { capture: true }
);

// Without this, errors escaping an RxJS subscription with no error handler are
// rethrown by reportUnhandledError, which drops the originating frames and collapses
// unrelated failures into one unactionable monitoring group. Report the real error
// (with its stack) to Dynatrace, then rethrow so existing global handlers still fire.
rxjsConfig.onUnhandledError = (error: unknown) => {
  const err = error instanceof Error ? error : new Error(String(error));
  try {
    // eslint-disable-next-line no-console
    console.error('[rxjs] Unhandled observable error', err);
    window.dtrum?.reportError(err);
  } finally {
    // Rethrow even if reporting above failed, so window.onerror / Dynatrace still fire.
    setTimeout(() => {
      throw err;
    });
  }
};

import('./Bootstrap').then(({ renderApp }) => {
  renderApp();
});
